/**
 * Функции обращения к API бэкенда модуля.
 *
 * Транспорт (единый клиент, режимы, интерсепторы, отмена по эпохе) живёт в
 * `httpClient.ts`; здесь — только эндпоинты и их типы.
 *
 * Ошибки нормализуются в `ApiError` из `@aseller/api-client` на ВСЕЙ границе, а
 * не в одной точке: `describePlatformError` и проверки `instanceof` в сторе
 * платформенного токена работают только с `ApiError`, а после переезда на
 * дуальный клиент сырой `AxiosError` дают все функции клиента. Точечная
 * нормализация оставила бы остальные молча необработанными.
 */

import type { AxiosError } from 'axios';
import { ApiError, toApiError } from '@aseller/api-client';
import { LOG_TAG } from '../lib/logTag';
import { apiClient, RetryAbortedError } from './httpClient';

export {
  MODULE_REFRESH_EXEMPT_URLS,
  PLATFORM_EXCHANGE_URL,
  PLATFORM_TOKEN_EXEMPT_URLS,
  apiClient,
  applyHostContext,
  disposeApiState,
  isStandaloneMode,
  platformTokenStore,
  RetryAbortedError,
  typedApi,
} from './httpClient';

// Реэкспорт: границу транспорта на `ApiError` нормализует `request()` ниже, и
// вызывающий код (виджеты) отличает 403 «не автор записи» от прочих отказов по
// `error.status` — без прямого импорта `@aseller/api-client`, эта точка входа
// уже единственная для всего, что относится к API.
export { ApiError };

export default apiClient;

/**
 * Отказ границы → `ApiError(status, url)` с исходной ошибкой в `cause`.
 *
 * `cause` обязателен: `ApiError` несёт только статус и url, а тело ответа
 * (`detail` бэкенда, blob с текстом ошибки экспорта) нужно показать
 * пользователю. Достаётся оно через `transportErrorOf`.
 *
 * `Error.cause` присваивается через каст: `lib: ES2020` этого поля не знает.
 *
 * `RetryAbortedError` проходит НАСКВОЗЬ: это не отказ сервера, а «мы ушли со
 * страницы», и весь смысл класса — чтобы вызывающий отличал одно от другого.
 * Обёрнутый в `ApiError(0)`, он стал бы неотличим от сетевого сбоя.
 */
async function request<T>(url: string, send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (error instanceof RetryAbortedError) throw error;
    const apiError = toApiError(error, url);
    const withCause = apiError as { cause?: unknown };
    if (withCause.cause === undefined && apiError !== error) withCause.cause = error;
    throw apiError;
  }
}

/** Исходная транспортная ошибка под `ApiError` — единственный носитель тела ответа. */
export function transportErrorOf(error: unknown): AxiosError | undefined {
  const cause = (error as { cause?: unknown } | null)?.cause;
  return (cause ?? error) as AxiosError | undefined;
}

/**
 * Логирование отказа запроса на границе UI.
 *
 * Отмена размонтированием (`RetryAbortedError`) уходит в `console.debug`, а не в
 * `console.error`, и причина не в аккуратности: в embed это консоль ЧУЖОЙ
 * страницы — страницы платформы. Каждый `unmount`, попавший в паузу повтора 503
 * или в ожидание refresh'а, писал бы туда «Save error» / «Failed to load Keepa
 * data», и чужая команда расследовала бы наш штатный teardown как сбой модуля.
 *
 * Именно `debug`, а не молчание: канал по умолчанию скрыт в DevTools, но
 * включается уровнем Verbose — то есть событие остаётся наблюдаемым, когда его
 * ищут. Проглотить отмену было бы молчаливым фолбэком; сам промис при этом
 * по-прежнему отклоняется `RetryAbortedError`, и вызывающий код отличает «мы
 * ушли со страницы» от «сервер отказал» ровно как раньше.
 */
export function logRequestFailure(scope: string, error: unknown, ...extra: unknown[]): void {
  if (error instanceof RetryAbortedError) {
    console.debug(`${LOG_TAG} ${scope} запрос отменён размонтированием модуля`, error);
    return;
  }
  console.error(scope, ...extra, error);
}

/* ── Keepa types ── */

export interface PackageDimensions {
  length: number | null;
  width: number | null;
  height: number | null;
}

export interface KeepaProductData {
  asin: string;
  buy_box_price: number | null;
  price_source: string | null;
  fba_fee: number | null;
  referral_fee: number | null;
  monthly_sales: number | null;
  package_weight_kg: number | null;
  volumetric_weight_kg: number | null;
  package_dimensions_cm: PackageDimensions | null;
  bsr_data: number[] | null;
  price_history: number[] | null;
  error: string | null;
}

export interface KeepaHistoryPoint {
  x: number;
  y: number;
}

export interface KeepaHistoryResponse {
  asin: string;
  title: string | null;
  price: KeepaHistoryPoint[];
  bsr: KeepaHistoryPoint[];
  window_days: number;
  price_points: number;
  bsr_points: number;
}

export interface MonthlyForecast {
  month: number;
  month_name: string;
  sales: number;
  revenue: number;
  profit: number;
  cumulative_profit: number;
}

export interface ProfitForecastResponse {
  profit_per_unit: number;
  batch_quantity: number;
  batch_profit: number;
  forecast: MonthlyForecast[];
  keepa_data: KeepaProductData | null;
  seasonality: number[] | null;
}

/* ── API functions ── */

export async function fetchKeepaData(
  asins: string[],
  signal?: AbortSignal,
): Promise<Record<string, KeepaProductData>> {
  return request('/api/keepa-data', async () => {
    const { data } = await apiClient.post('/api/keepa-data', { asins }, { signal });
    return data;
  });
}

export async function fetchKeepaHistory(
  asin: string,
  windowDays = 365,
): Promise<KeepaHistoryResponse> {
  return request('/api/keepa-history', async () => {
    const { data } = await apiClient.get('/api/keepa-history', {
      params: { asin, window_days: windowDays },
    });
    return data;
  });
}

export interface CompetitorData {
  asin: string;
  title?: string;
  image?: string;
  price?: number;
  brand?: string;
  rating?: string;
  reviews?: string;
  bsr?: string;
  clickShare?: number;
  monthlySales?: number;
}

export interface NicheData {
  nicheId: string;
  nicheTitle: string;
  products: CompetitorData[];
  count: number;
}

export interface CompetitorsResponse {
  niches: NicheData[];
  competitors: CompetitorData[];
  nicheTitle?: string;
  totalFound?: number;
}

export async function fetchCompetitors(
  asin: string,
): Promise<CompetitorsResponse> {
  return request('/proxy/competitors', async () => {
    const { data } = await apiClient.post('/proxy/competitors', { asin });
    return {
      niches: data.niches ?? [],
      competitors: data.competitors ?? [],
      nicheTitle: data.nicheTitle,
      totalFound: data.totalFound,
    };
  });
}

export async function fetchProfitForecast(params: {
  asin: string;
  competitor_asins?: string[];
  storage_fee_per_unit: number;
  purchase_price: number;
}): Promise<ProfitForecastResponse> {
  return request('/api/calculate-profit-forecast', async () => {
    const { data } = await apiClient.post(
      '/api/calculate-profit-forecast',
      params,
    );
    return data;
  });
}

/* ── Niches (Opportunity Explorer) ── */

/** Товар ниши. Доля кликов — 0..1. */
export interface NicheProduct {
  asin: string;
  brand: string | null;
  price: number | null;
  rating: number | null;
  reviews: number | null;
  click_share: number | null;
}

/**
 * Ниша Opportunity Explorer. Доли — 0..1 (0.047 = 4.7%); null — показателя у ниши нет.
 * `bound_at` есть только у ниши, сохранённой в расчёте.
 */
export interface Niche {
  niche_id: string;
  title: string;
  category: string | null;
  product_count: number | null;
  search_volume_t360: number | null;
  search_volume_growth_t180: number | null;
  search_volume_t90?: number | null;
  search_volume_growth_t90?: number | null;
  search_volume_growth_t360?: number | null;
  maximum_units_sold_t360?: number | null;
  search_conversion_rate: number | null;
  return_rate: number | null;
  avg_price: number | null;
  top5_click_share: number | null;
  top_products: NicheProduct[];
  bound_at?: string | null;
}

/** Ниши по ASIN или по ключу. 503 — поиск не настроен на стенде, 502 — POE не ответил. */
export async function searchNiches(query: { asin: string } | { keyword: string }): Promise<Niche[]> {
  return request('/api/niches', async () => {
    const { data } = await apiClient.get('/api/niches', { params: query, timeout: 90_000 });
    return data.niches as Niche[];
  });
}

/* ── Patents (IP Shield) ── */

export interface PatentFindings {
  design_patents: number;
  utility_patents: number;
  trademarks: number;
  ip_findings: number;
}

/**
 * Что IP Shield знает об ASIN. `checked: false` — не проверяли ни разу (штатный ответ).
 * `risk_source: 'manual'` — уровень риска выставил ревьюер IP Shield, а не авторасчёт.
 */
export interface PatentStatus {
  asin: string;
  checked: boolean;
  status?: string | null;
  risk_level?: string | null;
  risk_score?: number | null;
  risk_source?: 'manual' | 'auto' | null;
  checked_at?: string | null;
  age_days?: number | null;
  findings?: PatentFindings;
  report_url: string;
}

export interface PatentCheckStarted {
  asin: string;
  task_id: string;
  status: string;
  deduplicated: boolean;
  report_url: string;
}

/** Статус проверки ASIN. 401 — нет токена платформы, 503 — не настроено, 502 — IP Shield не ответил. */
export async function fetchPatentStatus(asin: string): Promise<PatentStatus> {
  return request(`/api/patents/${asin}/status`, async () => {
    const { data } = await apiClient.get(`/api/patents/${encodeURIComponent(asin)}/status`);
    return data as PatentStatus;
  });
}

/** Запуск проверки — ПЛАТНЫЙ прогон в IP Shield, только по явной кнопке. 429 — очередь полна. */
export async function startPatentCheck(asin: string): Promise<PatentCheckStarted> {
  return request(`/api/patents/${asin}/check`, async () => {
    const { data } = await apiClient.post(`/api/patents/${encodeURIComponent(asin)}/check`);
    return data as PatentCheckStarted;
  });
}

/* ── Parent search ── */

export interface ParentCandidate {
  asin: string;
  title: string | null;
  brand: string | null;
  type: string | null;
  item_type: string | null;
  categories: string[];
  rating: number | null;
  reviews: number | null;
  image: string | null;
  product_id: string | null;
  shops: string[];
  /** Детей в семействе parent (не `children`: antd Table читает его как вложенные строки). */
  children_count: number;
  relevance: number;
  match: 'visual' | 'attributes';
  reasons: string[];
  /** От какого числа данные Keepa по кандидату (кэш — неделя). */
  data_at: string | null;
}

export interface ParentSearchReport {
  target: { asin: string; title: string | null; brand: string | null; type: string | null; item_type: string | null; categories: string[]; image: string | null };
  pool_size: number;
  mode: 'combined' | 'attributes';
  candidates: ParentCandidate[];
  accounts: { shop: string; parents: number; best: number }[];
}

export interface ParentSearchParams {
  asin: string;
  count: number;
  min_relevance: number;
  rating_from: number;
  reviews_from: number;
  shops: string[];
  pool_limit: number;
}

/** Подбор parent по инвентарю платформы. 401 — нужен вход через платформу, 429 — лимит Keepa. */
export async function searchParents(params: ParentSearchParams): Promise<ParentSearchReport> {
  return request('/api/parent-search', async () => {
    const { data } = await apiClient.post('/api/parent-search', params, { timeout: 240_000 });
    return data as ParentSearchReport;
  });
}

/** Свежие атрибуты кандидатов мимо кэша — «Обновить» и перед привязкой. */
export async function refreshParentAttrs(asins: string[]): Promise<Record<string, (Partial<ParentCandidate> & { fetched_at?: string }) | null>> {
  return request('/api/parent-search/refresh', async () => {
    const { data } = await apiClient.post('/api/parent-search/refresh', { asins }, { timeout: 60_000 });
    return data.attrs;
  });
}

/** Аккаунты из инвентаря для фильтра и подключено ли визуальное сравнение. */
export async function fetchParentShops(): Promise<{ shops: string[]; visual: boolean }> {
  return request('/api/parent-search/shops', async () => {
    const { data } = await apiClient.get('/api/parent-search/shops', { timeout: 60_000 });
    return data;
  });
}

/* ── Save / Share / Export ── */

export async function saveResult(
  fileName: string,
  calcData: Record<string, unknown>,
): Promise<{ success: boolean; message: string }> {
  return request('/api/save-result', async () => {
    const { data } = await apiClient.post('/api/save-result', {
      fileName,
      data: calcData,
    });
    return data;
  });
}

export async function createShareLink(
  fileName: string,
): Promise<{ share_id: string; url: string }> {
  return request('/api/share', async () => {
    const { data } = await apiClient.post('/api/share', { fileName });
    return data;
  });
}

export async function fetchSharedResult(
  shareId: string,
): Promise<Record<string, unknown>> {
  const url = `/api/share/${shareId}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

/* ── Фото товара ── */

/** ASIN → адрес фото листинга; `null` — у товара фото нет. */
export type ProductImages = Record<string, string | null>;

export async function fetchProductImages(asins: string[]): Promise<ProductImages> {
  return request('/api/product-images', async () => {
    const { data } = await apiClient.post<{ images: ProductImages }>('/api/product-images', { asins });
    return data.images;
  });
}

/** Фото товара шар-ссылки: доступно анонимно, ASIN берётся из самой записи. */
export async function fetchShareImage(shareId: string): Promise<string | null> {
  const url = `/api/share/${shareId}/image`;
  return request(url, async () => {
    const { data } = await apiClient.get<{ asin: string; image_url: string | null }>(url);
    return data.image_url;
  });
}

export async function exportXlsx(
  payload: Record<string, unknown>,
): Promise<Blob> {
  return request('/api/export-xlsx', async () => {
    const { data } = await apiClient.post('/api/export-xlsx', payload, {
      responseType: 'blob',
    });
    return data;
  });
}

/* ── Вход через платформу (SSO) ── */

export interface SsoConfig {
  enabled: boolean;
  /** Страница согласия на фронте платформы; есть только когда `enabled`. */
  authorize_url?: string | null;
  /** `code` сервиса из реестра платформы — он же параметр `client` ссылки. */
  client?: string | null;
}

export interface SsoTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export async function fetchSsoConfig(): Promise<SsoConfig> {
  return request('/api/platform/sso/config', async () => {
    const { data } = await apiClient.get('/api/platform/sso/config');
    return data;
  });
}

/** Обмен одноразового кода на сессию модуля. Сервисный секрет живёт на бэкенде. */
export async function exchangeSsoCode(code: string): Promise<SsoTokens> {
  return request('/api/platform/sso/exchange', async () => {
    const { data } = await apiClient.post('/api/platform/sso/exchange', { code });
    return data;
  });
}

/* ── Вход MCP-клиента через платформу ── */

/** Итог входа на платформе для MCP-клиента: ровно одно из `code` / `error`. */
export type McpCompletion =
  | { txn: string; code: string; error?: undefined }
  | { txn: string; error: string; code?: undefined };

export interface McpRedirect {
  /** Куда вернуть браузер: loopback клиента, `https://claude.ai/…` или `cursor://…`. */
  redirect_url: string;
}

const MCP_COMPLETE_URL = '/api/mcp-oauth/complete';

/**
 * Завершение MCP-входа: бэкенд меняет код на пару токенов для клиента и отдаёт адрес
 * возврата. Cookie привязки браузера (её ставит экран согласия) уходит сама — запрос
 * same-origin. Без адреса в ответе вести браузер некуда, и это отказ, а не пустой переход.
 */
export async function completeMcpAuthorization(completion: McpCompletion): Promise<McpRedirect> {
  return request(MCP_COMPLETE_URL, async () => {
    const { data } = await apiClient.post<Partial<McpRedirect>>(MCP_COMPLETE_URL, completion);
    if (typeof data?.redirect_url !== 'string' || !data.redirect_url) {
      throw new Error(`${MCP_COMPLETE_URL}: в ответе нет redirect_url`);
    }
    return { redirect_url: data.redirect_url };
  });
}

/* ── History / User Results ── */

/** Кем сохранена запись: человеком в UI или агентом через MCP (колонка `source`). */
export type RecordSource = 'ui' | 'mcp';

/**
 * Метрики расчёта для таблицы истории — сохранённая экономика, без пересчёта.
 * Прибыль, маржа и ROI — «с учётом всех затрат (без бюджета на запуск)».
 * null у поля — показать нечего (не посчитано), это не ноль.
 */
export interface HistoryMetrics {
  price: number | null;
  monthly_sales: number | null;
  cogs: number | null;
  profit_unit: number | null;
  margin: number | null;
  roi: number | null;
  profit_month: number | null;
  conversion: number | null;
  returns: number | null;
  ppc_share: number | null;
  order_price: number | null;
  ad_per_unit: number | null;
  batch: number | null;
  batch_cost: number | null;
  batch_profit: number | null;
  batch_margin: number | null;
  batch_roi: number | null;
  max_purchase_price: number | null;
}

export interface HistoryVersion {
  /** Первичный ключ записи — адресует GET/DELETE /api/results/{id}. */
  id?: number;
  fileName: string;
  parent_asin: string;
  timestamp: string;
  is_variation: boolean;
  variation_idea: string;
  selling_price: number | null;
  monthly_sales: number | null;
  owner_id?: string;
  /** Нет поля — ответ старого формата, запись человеческая. */
  source?: RecordSource;
  /** Чужая запись, присвоенная по share-ссылке (только у обычного пользователя). */
  claimed?: boolean;
  /** Нет поля или null — у записи нет сохранённой экономики (старый формат). */
  metrics?: HistoryMetrics | null;
  /** Фото карточки биржи, сохранённое с расчётом; у расчёта по ASIN — null (фото даёт Keepa). */
  image_url?: string | null;
}

export interface HistoryGroup {
  asin: string;
  latest_timestamp: string;
  versions_count: number;
  versions: HistoryVersion[];
}

export async function fetchUserResults(): Promise<{ groups: HistoryGroup[] }> {
  return request('/api/user-results', async () => {
    const { data } = await apiClient.get('/api/user-results');
    return data;
  });
}

export async function claimShare(shareId: string): Promise<{ success: boolean }> {
  return request('/api/claim-share', async () => {
    const { data } = await apiClient.post('/api/claim-share', { share_id: shareId });
    return data;
  });
}

/** Свой сохранённый расчёт по id записи (ссылка `?result=<id>` из таблицы истории). */
export async function loadResultById(resultId: number): Promise<Record<string, unknown>> {
  const url = `/api/results/${resultId}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

export async function loadSavedResult(filename: string): Promise<Record<string, unknown>> {
  // Имя расчёта — свободный текст пользователя, и в сегменте URL его надо
  // кодировать: `#` в имени браузер считает началом фрагмента и до сервера его
  // не доносит. Расчёт «План #1» сохранялся, был виден в истории и не
  // открывался никогда — приходил 404 по обрезанному имени.
  const url = `/api/saved-results/${encodeURIComponent(filename)}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

/* ── Карточка товара платформы и история расчётов по ней ── */

/**
 * Поля калькулятора, добытые из карточки товара платформы.
 *
 * Все поля опциональны намеренно: бэкенд не заполняет то, чего в карточке нет или
 * что пришло нулём. На живых данных вес и габариты отсутствуют примерно у трети
 * товаров, а цена бывает нулевой — подставленный ноль молча исказил бы
 * себестоимость и объёмный вес.
 */
export interface ProductCardFields {
  asin?: string;
  sellingPrice?: number;
  referralFee?: number;
  fbaFee?: number;
  height?: number;
  width?: number;
  length?: number;
  unitWeightKg?: number;
  purchasePrice?: number;
  priceInYuan?: number;
  yuanToDollarRate?: number;
  batchQuantity?: number;
  shippingCostPerKg?: number;
}

/** Карточка биржи поставщиков, уже в полях калькулятора (`fields` — размеры в дюймах). */
export interface ExchangeCard {
  xid: string;
  title: string | null;
  image: string | null;
  supplier: string | null;
  min_lot: number | null;
  unit_cm: { length: number; width: number; height: number } | null;
  box: { amount: number | null; length_cm: number | null; width_cm: number | null; height_cm: number | null; gross_kg: number | null };
  fields: ProductCardFields;
}

/** Карточка биржи по номеру. 404 — нет или не видна, 401 — нет токена платформы. */
export async function fetchExchangeCard(xid: string): Promise<ExchangeCard> {
  const url = `/api/exchange-card/${encodeURIComponent(xid)}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data as ExchangeCard;
  });
}

export async function fetchProductCard(productId: string): Promise<ProductCardFields> {
  const url = `/api/product-card/${encodeURIComponent(productId)}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

/** Запись истории в списке — без payload: он тяжёлый и в списке не нужен. */
export interface ProductCalculationListItem {
  id: number;
  title: string;
  owner_name: string;
  /** Нет поля — ответ старого формата, запись человеческая. */
  source?: RecordSource;
  created_at: string;
  /**
   * `null` у записи, которую ни разу не перезаписывали.
   *
   * Отличать «не менялась» от «изменена» по наличию значения, а не сравнением дат:
   * бэкенд намеренно не подставляет сюда дату создания.
   */
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface ProductCalculationDetail extends ProductCalculationListItem {
  product_id: string;
  owner_id: string;
  payload: Record<string, unknown>;
}

function productCalculationsUrl(productId: string): string {
  return `/api/product-calculations/${encodeURIComponent(productId)}`;
}

export async function fetchProductCalculations(
  productId: string,
): Promise<ProductCalculationListItem[]> {
  const url = productCalculationsUrl(productId);
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

export async function saveProductCalculation(
  productId: string,
  title: string,
  payload: Record<string, unknown>,
): Promise<ProductCalculationDetail> {
  const url = productCalculationsUrl(productId);
  return request(url, async () => {
    const { data } = await apiClient.post(url, { title, payload });
    return data;
  });
}

/**
 * Перезаписать существующую запись истории.
 *
 * Нужно для сценария «загрузил расчёт, поправил, сохранил под тем же именем»: без
 * этого каждое сохранение плодило бы запись-двойник с тем же названием. Право —
 * только у автора: бэкенд отвечает 403 на чужую запись.
 */
export async function updateProductCalculation(
  productId: string,
  calcId: number,
  title: string,
  payload: Record<string, unknown>,
): Promise<ProductCalculationDetail> {
  const url = `${productCalculationsUrl(productId)}/${calcId}`;
  return request(url, async () => {
    const { data } = await apiClient.put(url, { title, payload });
    return data;
  });
}

export async function loadProductCalculation(
  productId: string,
  calcId: number,
): Promise<ProductCalculationDetail> {
  const url = `${productCalculationsUrl(productId)}/${calcId}`;
  return request(url, async () => {
    const { data } = await apiClient.get(url);
    return data;
  });
}

export async function deleteProductCalculation(
  productId: string,
  calcId: number,
): Promise<{ success: boolean }> {
  const url = `${productCalculationsUrl(productId)}/${calcId}`;
  return request(url, async () => {
    const { data } = await apiClient.delete(url);
    return data;
  });
}

/* ── Admin: user management (CU local-auth) ── */

export interface AdminUser {
  id: number;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string | null;
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  return request('/api/auth/users', async () => {
    const { data } = await apiClient.get('/api/auth/users');
    return data.users;
  });
}

export async function createAdminUser(payload: {
  email: string;
  password: string;
  is_admin: boolean;
}): Promise<AdminUser> {
  return request('/api/auth/users', async () => {
    const { data } = await apiClient.post('/api/auth/users', payload);
    return data;
  });
}

export async function setAdminUserRole(
  userId: number,
  isAdmin: boolean,
): Promise<AdminUser> {
  const url = `/api/auth/users/${userId}/role`;
  return request(url, async () => {
    const { data } = await apiClient.patch(url, { is_admin: isAdmin });
    return data;
  });
}

export async function deactivateAdminUser(userId: number): Promise<void> {
  const url = `/api/auth/users/${userId}`;
  return request(url, async () => {
    await apiClient.delete(url);
  });
}

export async function changePassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<{ message: string }> {
  return request('/api/auth/change-password', async () => {
    const { data } = await apiClient.post('/api/auth/change-password', payload);
    return data;
  });
}
